// AIMS PWA - offline inspection capture (AIMS-Offline-Inspection.md §9 Phase 3).
//
// Cache-backed reads (so the visit screen + inspection form render with no
// signal) and the outbox write (completed inspections queued for the Phase 4
// sync engine). All reads come from the Dexie stores populated by "Take offline"
// (takeOffline.js); the outbox is the same store the batch push will drain.

import db from './db';
import dayjs from 'dayjs';

const norm = (s) => (s == null ? '' : String(s));
const STATUS_TITLE = { PASS: 'Pass', FAIL: 'Fail', NTI: 'NTI' };

// ── Cache-backed reads ──────────────────────────────────────────────────────

// Reconstruct a visitData-shaped object from the cached pool so the visit
// inspection screen renders offline. Mirrors the shape the server's
// GET /api/schedule/visits/:id returns for the fields the screen uses: assets
// carry `cat_no` (snake) + assetId/item/manufacturer/model/siteLocation/status.
export async function getCachedVisit(visitId) {
  const meta = await db.syncMeta.get(visitId);
  if (!meta) return null;
  const rows = await db.assets.where('visitId').equals(visitId).toArray();
  const assets = rows.map((a) => ({
    id: a.catNo,                 // stable local key (screen only needs uniqueness)
    cat_no: a.catNo,
    assetId: a.assetId || '',
    item: a.item || '',
    type: a.type || '',
    manufacturer: a.manufacturer || '',
    model: a.model || '',
    siteLocation: a.siteLocation || '',
    inspectionStatus: a.inspectionStatus || null,
    lastInspectionDate: a.lastInspectionDate || null,
    completedThisVisit: !!a.completedThisVisit,
    inspectedBy: a.inspectedBy || null,
  }));
  return {
    id: visitId,
    company: meta.company || '',
    status: meta.status || null,
    offline: true,               // marker so the screen can show an offline banner
    assets,
  };
}

// item → [auditType] from the cached type map.
export async function getCachedAuditTypesForItem(item) {
  if (!item) return [];
  const rows = await db.typeMap.where('item').equals(item).toArray();
  return rows.map((r) => r.auditType).filter(Boolean);
}

// Cached checklist template items ([{item, result:null}]) for an audit type.
export async function getCachedChecklist(auditType) {
  const t = await db.checklistTemplates.get(auditType);
  return t && Array.isArray(t.items) ? t.items.map((i) => ({ ...i })) : [];
}

// ── Outbox write ─────────────────────────────────────────────────────────────

// Queue a completed inspection for later sync. Returns the minted
// clientInspectionId. Also optimistically stamps the cached asset row so the
// visit list shows "done by me" immediately (append-only; never destructive).
export async function saveInspectionToOutbox({ visitId, payload }) {
  const clientInspectionId =
    (window.crypto && window.crypto.randomUUID)
      ? window.crypto.randomUUID()
      : 'cid-' + Date.now() + '-' + Math.round(Math.random() * 1e9);

  const inspectedBy = payload.inspectedBy || localStorage.getItem('username') || null;
  const record = {
    clientInspectionId,
    visitId,
    siteVisitId: visitId,
    catNo: norm(payload.catNo),
    customer: payload.customer || null,
    auditType: payload.auditType || null,
    inspectedBy,
    inspectionDate: payload.inspectionDate || null,
    reinspectionDue: payload.reinspectionDue || null,
    checklist: payload.checklist || [],
    achievedScore: payload.achievedScore != null ? payload.achievedScore : null,
    overallResult: payload.overallResult || null,
    notes: payload.notes || null,
    firstTimeInspected: payload.firstTimeInspected === 1 ? 1 : 0,
    syncedAt: null,             // pending until the sync engine acks it
    createdAt: Date.now(),
  };

  await db.transaction('rw', db.outbox, db.assets, async () => {
    await db.outbox.add(record);
    // Optimistic local status so the visit screen reflects the work at once.
    const key = [visitId, norm(payload.catNo)];
    const existing = await db.assets.get(key);
    if (existing) {
      const titled = STATUS_TITLE[String(payload.overallResult || '').toUpperCase()] || payload.overallResult || existing.inspectionStatus;
      await db.assets.update(key, {
        inspectionStatus: titled,
        // Store as DD/MM/YYYY to match the online visit screen's date handling.
        lastInspectionDate: payload.inspectionDate ? dayjs(payload.inspectionDate).format('DD/MM/YYYY') : existing.lastInspectionDate,
        completedThisVisit: true,
        inspectedBy,
      });
    }
  });

  return clientInspectionId;
}

// Count of inspections still awaiting upload (across all visits).
export async function getPendingInspectionCount() {
  try {
    return await db.outbox.filter((o) => !o.syncedAt).count();
  } catch (_) {
    return 0;
  }
}
